import { Message, MessageTemplateSnapshot } from '../../domain/conversations/types';
import { CompanyPhoneNumber } from '../../domain/phone-numbers/types';
import {
  Template,
  findComponent,
  renderTemplateBody,
  templateBody,
  templateButtons,
  templateFooter,
  templateHeader,
} from '../../domain/templates/types';

const minutesAgo = (minutes: number) => new Date(Date.now() - minutes * 60_000).toISOString();
const hoursAgo = (hours: number) => minutesAgo(hours * 60);

export const fakePhoneNumbers: CompanyPhoneNumber[] = [
  {
    companyPhoneNumber: '551130304500',
    displayPhoneNumber: '+55 11 3030-4500',
    verifiedName: 'Netpark Comunicações SP',
    qualityRating: 'GREEN',
    roleLabel: 'Treinamento e Desenvolvimento',
  },
  {
    companyPhoneNumber: '552135551200',
    displayPhoneNumber: '+55 21 3555-1200',
    verifiedName: 'Netpark Comunicações RJ',
    qualityRating: 'GREEN',
    roleLabel: 'Recrutamento e Seleção',
  },
  {
    companyPhoneNumber: '551130304520',
    displayPhoneNumber: '+55 11 3030-4520',
    verifiedName: 'Avisos operacionais',
    qualityRating: 'YELLOW',
    roleLabel: null,
  },
];

export interface FakeConversationSeed {
  phoneNumber: string;
  name: string;
  chosenName?: string | null;
  messages: Message[];
  readAt?: string | null;
}

let messageId = 0;
const nextId = () => `msg-${++messageId}`;

export const fakeConversations: FakeConversationSeed[] = [
  {
    phoneNumber: '5511988124471',
    name: 'Camila Ferreira',
    messages: [
      {
        id: nextId(),
        direction: 'INBOUND',
        type: 'TEXT',
        content: { text: 'Oi! Recebi o convite do treinamento e queria confirmar o horário 😊' },
        messageAt: minutesAgo(80),
      },
      {
        id: nextId(),
        direction: 'OUTBOUND',
        type: 'TEXT',
        content: {
          text: 'Olá Camila! A turma começa quinta, às 9h, na sala 2. Consegue participar?',
        },
        status: 'READ',
        messageAt: minutesAgo(68),
      },
      {
        id: nextId(),
        direction: 'INBOUND',
        type: 'TEXT',
        content: { text: 'Consigo sim! Quinta de manhã funciona pra mim.' },
        messageAt: minutesAgo(60),
      },
      {
        id: nextId(),
        direction: 'INBOUND',
        type: 'DOCUMENT',
        content: {
          media: {
            mediaId: 'fake-media-comprovante',
            mimeType: 'application/pdf',
            fileName: 'comprovante-inscricao-camila-ferreira.pdf',
          },
        },
        messageAt: minutesAgo(58),
      },
    ],
  },
  {
    phoneNumber: '5511997302210',
    name: 'Rafael Souza',
    messages: [
      {
        id: nextId(),
        direction: 'OUTBOUND',
        type: 'TEMPLATE',
        content: {
          templateName: 'comunicado_interno',
          templateLanguage: 'pt_BR',
          text: 'Olá Rafael! Aqui é da equipe de Treinamento e Desenvolvimento da Netpark. O treinamento de Segurança Operacional está com inscrições abertas e você faz parte da turma indicada.\n\nNetpark Comunicações',
          templateVariables: { '1': 'Rafael', '2': 'Segurança Operacional' },
          template: {
            category: 'UTILITY',
            body: 'Olá {{1}}! Aqui é da equipe de Treinamento e Desenvolvimento da Netpark. O treinamento de {{2}} está com inscrições abertas e você faz parte da turma indicada.',
            bodyRendered:
              'Olá Rafael! Aqui é da equipe de Treinamento e Desenvolvimento da Netpark. O treinamento de Segurança Operacional está com inscrições abertas e você faz parte da turma indicada.',
            footer: 'Netpark Comunicações',
            buttons: [{ text: 'Quero participar' }, { text: 'Não consigo agora' }],
          },
        },
        status: 'READ',
        messageAt: hoursAgo(4),
      },
      {
        id: nextId(),
        direction: 'INBOUND',
        type: 'TEXT',
        content: { text: 'Bom dia! Sim, quero participar do treinamento.' },
        messageAt: hoursAgo(3),
      },
    ],
  },
  {
    phoneNumber: '5511981207788',
    name: 'Beatriz Lima',
    messages: [
      {
        id: nextId(),
        direction: 'OUTBOUND',
        type: 'TEMPLATE',
        content: {
          templateName: 'agendamento_turma',
          templateLanguage: 'pt_BR',
          text: 'Agendamento da turma\n\nOi Beatriz, tudo bem? Sua turma ficou marcada para terça às 15h. Você confirma esse horário?',
          templateVariables: { '1': 'Beatriz', '2': 'terça', '3': '15h' },
          template: {
            category: 'UTILITY',
            headerFormat: 'TEXT',
            header: 'Agendamento da turma',
            body: 'Oi {{1}}, tudo bem? Sua turma ficou marcada para {{2}} às {{3}}. Você confirma esse horário?',
            bodyRendered:
              'Oi Beatriz, tudo bem? Sua turma ficou marcada para terça às 15h. Você confirma esse horário?',
          },
        },
        status: 'READ',
        messageAt: hoursAgo(34),
      },
      {
        id: nextId(),
        direction: 'INBOUND',
        type: 'TEXT',
        content: { text: 'Oi! Pode ser terça à tarde?' },
        messageAt: hoursAgo(33),
      },
      {
        id: nextId(),
        direction: 'OUTBOUND',
        type: 'TEXT',
        content: { text: 'Perfeito, terça 15h então. Te envio o link!' },
        status: 'DELIVERED',
        messageAt: hoursAgo(32),
      },
    ],
  },
  {
    phoneNumber: '5521991453300',
    name: 'João Mendes',
    messages: [
      {
        id: nextId(),
        direction: 'INBOUND',
        type: 'TEXT',
        content: { text: 'Recebi o comunicado de vocês, quero participar!' },
        messageAt: hoursAgo(6),
      },
    ],
  },
  {
    phoneNumber: '5511970019922',
    name: 'Larissa Prado',
    messages: [
      {
        id: nextId(),
        direction: 'OUTBOUND',
        type: 'TEMPLATE',
        content: {
          templateName: 'certificado_disponivel',
          templateLanguage: 'pt_BR',
          text: 'Larissa, seu certificado do treinamento de Atendimento ao Cliente já está disponível. Quer que a gente envie por aqui?',
          templateVariables: { '1': 'Larissa', '2': 'Atendimento ao Cliente' },
          template: {
            category: 'UTILITY',
            body: '{{1}}, seu certificado do treinamento de {{2}} já está disponível. Quer que a gente envie por aqui?',
            bodyRendered:
              'Larissa, seu certificado do treinamento de Atendimento ao Cliente já está disponível. Quer que a gente envie por aqui?',
          },
        },
        status: 'SENT',
        messageAt: hoursAgo(72),
      },
    ],
  },
];

export const fakeTemplates: Template[] = [
  {
    id: 't1',
    name: 'comunicado_interno',
    language: 'pt_BR',
    category: 'UTILITY',
    status: 'APPROVED',
    ownerRoles: ['TD'],
    components: [
      {
        type: 'BODY',
        text: 'Olá {{1}}! Aqui é da equipe de Treinamento e Desenvolvimento da Netpark. O treinamento de {{2}} está com inscrições abertas e você faz parte da turma indicada.',
        example: { body_text: [['Rafael', 'Segurança Operacional']] },
      },
      { type: 'FOOTER', text: 'Netpark Comunicações' },
      {
        type: 'BUTTONS',
        buttons: [{ text: 'Quero participar' }, { text: 'Não consigo agora' }],
      },
    ],
  },
  {
    id: 't2',
    name: 'agendamento_turma',
    language: 'pt_BR',
    category: 'UTILITY',
    status: 'APPROVED',
    ownerRoles: ['TD'],
    components: [
      { type: 'HEADER', format: 'TEXT', text: 'Agendamento da turma' },
      {
        type: 'BODY',
        text: 'Oi {{1}}, tudo bem? Sua turma ficou marcada para {{2}} às {{3}}. Você confirma esse horário?',
        example: { body_text: [['Beatriz', 'terça', '15h']] },
      },
    ],
  },
  {
    id: 't3',
    name: 'lembrete_treinamento',
    language: 'pt_BR',
    category: 'UTILITY',
    status: 'PENDING',
    ownerRoles: ['TD'],
    components: [
      {
        type: 'BODY',
        text: 'Olá {{1}}! Passando para lembrar do seu treinamento amanhã às {{2}}. Qualquer imprevisto, é só nos avisar por aqui.',
        example: { body_text: [['Camila', '9h']] },
      },
    ],
  },
  {
    id: 't4',
    name: 'certificado_disponivel',
    language: 'pt_BR',
    category: 'UTILITY',
    status: 'APPROVED',
    ownerRoles: ['TD'],
    components: [
      {
        type: 'BODY',
        text: '{{1}}, seu certificado do treinamento de {{2}} já está disponível. Quer que a gente envie por aqui?',
        example: { body_text: [['Larissa', 'Atendimento ao Cliente']] },
      },
    ],
  },
  {
    id: 't5',
    name: 'divulgacao_programa',
    language: 'pt_BR',
    category: 'MARKETING',
    status: 'REJECTED',
    rejectedReason: 'INCORRECT_CATEGORY',
    ownerRoles: ['RS'],
    components: [
      {
        type: 'BODY',
        text: '🚀 A Netpark abriu novas turmas de capacitação! Confira os programas e inscreva-se pelo link.',
      },
      { type: 'FOOTER', text: 'Responda SAIR para não receber mais mensagens' },
    ],
  },
  {
    id: 't6',
    name: 'codigo_verificacao',
    language: 'pt_BR',
    category: 'AUTHENTICATION',
    status: 'APPROVED',
    ownerRoles: ['TD', 'RS'],
    components: [
      {
        type: 'BODY',
        text: '{{1}} é o seu código de verificação Netpark. Não compartilhe com ninguém.',
        example: { body_text: [['482913']] },
      },
    ],
  },
  {
    id: 't7',
    name: 'aviso_operacional',
    language: 'pt_BR',
    category: 'UTILITY',
    status: 'APPROVED',
    ownerRoles: ['TD'],
    components: [
      {
        type: 'BODY',
        text: 'Comunicado da Netpark: houve uma mudança na escala desta semana. Procure sua liderança para conferir os detalhes.',
      },
      { type: 'FOOTER', text: 'Netpark Comunicações' },
    ],
  },
];

export const fakeNextMessageId = nextId;

export function fakeTemplateSnapshot(
  templateName: string,
  variables: Record<string, string> = {}
): MessageTemplateSnapshot | undefined {
  const template = fakeTemplates.find(item => item.name === templateName);

  if (!template) {
    return undefined;
  }

  const format = findComponent(template, 'HEADER')?.format;
  const header = templateHeader(template);
  const body = templateBody(template);
  const footer = templateFooter(template);
  const buttons = templateButtons(template);

  return {
    category: template.category,
    ...(format ? { headerFormat: format } : {}),
    ...(header ? { header } : {}),
    ...(body ? { body, bodyRendered: renderTemplateBody(template, variables) } : {}),
    ...(footer ? { footer } : {}),
    ...(buttons.length ? { buttons: buttons.map(button => ({ text: button.text })) } : {}),
  };
}

export function fakeTemplateText(snapshot: MessageTemplateSnapshot): string {
  return [snapshot.header, snapshot.bodyRendered, snapshot.footer].filter(Boolean).join('\n\n');
}
